import { FC, useEffect, useState } from 'react';
import { asset } from '../../services/assetResolverService';
import { Link } from '../link';
import { DynamicPaneContent } from './layouts';
import { TrackedLink } from '../link';
import { ZoneContainer } from '../ads/zone';
import { ModerationReportContextType } from '../../../common/moderationTypes';
import { ModerationReportModal } from '../moderation/ModerationReportModal';
import { useModerationReportingEnabled } from '../moderation/useModerationReportingEnabled';

const CSE_SCRIPT_URL = 'https://cse.google.com/cse.js?cx=017387033638754823434:21sj9_g7qgo';

let cseScriptState: 'idle' | 'loading' | 'loaded' | 'failed' = 'idle';
const cseCallbacks: Array<(success: boolean) => void> = [];

function loadCSEScript(onComplete: (success: boolean) => void): void {
  if (cseScriptState === 'loaded') {
    onComplete(true);
    return;
  }
  if (cseScriptState === 'failed') {
    onComplete(false);
    return;
  }

  cseCallbacks.push(onComplete);

  if (cseScriptState === 'loading') {
    return;
  }

  cseScriptState = 'loading';
  const script = document.createElement('script');
  script.src = CSE_SCRIPT_URL;
  script.async = true;
  script.onload = () => {
    cseScriptState = 'loaded';
    cseCallbacks.forEach((cb) => cb(true));
    cseCallbacks.length = 0;
  };
  script.onerror = () => {
    cseScriptState = 'failed';
    script.remove();
    cseCallbacks.forEach((cb) => cb(false));
    cseCallbacks.length = 0;
  };
  document.body.appendChild(script);
}

interface NotFoundPageProps {
  location?: {
    pathname?: string;
    search?: string;
  };
}

const NotFoundPage: FC<NotFoundPageProps> = (props) => {
  const [cseReady, setCseReady] = useState(cseScriptState === 'loaded');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const showReportModal = useModerationReportingEnabled();
  const hasSearch = props.location?.search;
  const path = props.location?.pathname || '';

  useEffect(() => {
    if (hasSearch && cseScriptState !== 'loaded') {
      loadCSEScript((success) => {
        if (success) {
          setCseReady(true);
        }
      });
    }
  }, [hasSearch]);

  return (
    <div>
      <div>
        <DynamicPaneContent>
          <div>
            <div className="slds-grid">
              <div className="slds-small-size--1-of-1 slds-medium-size--3-of-6">
                <h1 className="slds-text-heading--large slds-m-bottom--large">Page Not Found</h1>
                <p>
                  Uh oh! It appears that the page you have requested does not exist. If you think this page really
                  should have been here,{' '}
                  {showReportModal ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsReportModalOpen(true);
                      }}
                    >
                      please let us know
                    </a>
                  ) : (
                    <a
                      href={`mailto:support@raider.io?subject=Missing%20Page%20on%20Raider.IO&body=Page:%20${encodeURIComponent(
                        path
                      )}`}
                    >
                      please let us know
                    </a>
                  )}
                  .
                </p>
                <div className="slds-m-top--large">Where would you like to go next?</div>
                <ul className="slds-list--dotted slds-list--vertical">
                  <li className="slds-item">
                    <Link className="text-white" to="/">
                      Home Page
                    </Link>
                  </li>
                  <li className="slds-item">
                    <TrackedLink className="text-white" href="https://support.raider.io" target="_blank">
                      FAQ
                    </TrackedLink>
                  </li>
                </ul>
              </div>
              <div className="rio-hidden--small slds-size--3-of-6 slds-text-align--left slds-p-left--large">
                <img src={asset('/images/site/error-page.png')} style={{ width: '400px' }} />
              </div>
            </div>

            {hasSearch && cseReady && (
              <div dangerouslySetInnerHTML={{ __html: '<gcse:searchresults-only></gcse:searchresults-only>' }} />
            )}
          </div>
          <div>
            <ZoneContainer tag="section" className="slds-text-align--center" type="mrec_sidebar_top_short" />
          </div>
        </DynamicPaneContent>
      </div>
      {showReportModal && (
        <ModerationReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          contextType={ModerationReportContextType.OTHER}
          entityId={0}
          sourceUrl={path}
        />
      )}
    </div>
  );
};

export default NotFoundPage;
